import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { CommentFormComponent } from '@app/shared/components/comment/comment-form/comment-form.component';
import { FormControlConfigType } from '@app/core/enums/form-control-config-type';
import { IStringFormControlConfig } from '@app/core/interfaces/form-control-config';
import { IFloatingPopupInstance } from '@app/main/main-content/floating-popup-manager/interfaces/floating-popup-instance';
import { FloatingPopupService } from '@app/main/main-content/floating-popup-manager/services/floating-popup.service';
import { FpLoggerNoiseCommentsActionType } from '@app/main/leak-detection/floating-popups/floating-popup-logger-noise/tabs/fp-logger-noise-comments/services/fp-logger-noise-comments/fp-logger-noise-comments.actions';
import { FpLoggerNoiseCommentsStore } from '@app/main/leak-detection/floating-popups/floating-popup-logger-noise/tabs/fp-logger-noise-comments/services/fp-logger-noise-comments/fp-logger-noise-comments.store';
import { toSignal } from '@angular/core/rxjs-interop';
import { CommentAuthorComponent } from '@app/shared/components/comment/comment-author/comment-author.component';
import { FormattingDataService } from '@app/core/services/formatting-data/formatting-data.service';
import { TranslateDirective } from '@ngx-translate/core';
import { NgxPermissionsModule } from 'ngx-permissions';
import { PermissionName } from '@app/core/permissions/enums/permission-name.enum';
import { CommentCardComponent } from '@app/shared/components/comment/comment-card/comment-card.component';

@Component({
  selector: 'gc-fp-logger-noise-comments',
  standalone: true,
  imports: [
    CommentFormComponent,
    CommentAuthorComponent,
    TranslateDirective,
    NgxPermissionsModule,
    CommentCardComponent,
  ],
  templateUrl: './fp-logger-noise-comments.component.html',
  styleUrls: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FpLoggerNoiseCommentsComponent {
  public readonly currentTabStore = inject(FpLoggerNoiseCommentsStore);
  protected readonly floatingPopupService = inject(FloatingPopupService);
  protected readonly formattingData = inject(FormattingDataService);
  protected readonly PermissionName = PermissionName;

  public popup = input<IFloatingPopupInstance>();

  public commentSaved = toSignal(this.currentTabStore.saved$, { initialValue: false });

  public readonly comment = toSignal(this.currentTabStore.comment$, {
    initialValue: null,
  });

  public readonly commentAuthor = computed<string>(
    () => this.comment()?.author || this.formattingData.NO_DATA_VALUE_PLACEHOLDER_KEY
  );

  public readonly commentModifiedAt = computed<string>(() => {
    const modifiedAt = this.comment()?.modifiedAt;
    if (!modifiedAt) {
      return this.formattingData.NO_DATA_VALUE_PLACEHOLDER_KEY;
    }

    return this.formattingData.formatDate(modifiedAt);
  });

  public readonly message = computed<string>(
    () => this.comment()?.content || this.formattingData.NO_DATA_VALUE_PLACEHOLDER_KEY
  );

  public readonly commentFacade: IStringFormControlConfig = {
    id: `logger-noise-comment-${this.popup()?.id}`,
    type: FormControlConfigType.String,
    defaultValue: '',
    getValue: () => this.currentTabStore.commentContent$,
    valueChange: (value: string) =>
      this.currentTabStore.dispatch({
        type: FpLoggerNoiseCommentsActionType.UpdateComment,
        content: value,
      }),
  };
}
